// @flow
import type { Store } from 'redux';
import moment from 'moment';
import deviceType from 'Common/app/getDeviceType';
import { type Receiver } from '../../auth/types';
import type { Action as VmAction, VmEvent } from './types';
import { post, get } from '../../utilities/fetchUtils';
import type { State as AppState, BaseAction, ThunkAction, Dispatch } from '../../types';
import { getClientId, getUniqueId } from './utils';
import {
  addVmEventToStorage,
  addBillingEventToStorage,
  removeVmEventFromStorage,
  removeBillingEventFromStorage,
} from '../actions';

// On CAST receivers running from DEV servers we use a relative URL for Sling analytics
// because domain is different from Dany Web and we have to use nginx proxy-pass configured
// on CAST DEV server. Non-DEV Cast receivers will have SAME domain names as Dany Web,
// hence will go to the same URL for Sling analytics as Dany Web, utilizing Dany servers
// nginx proxy-pass, which is already configured.
const getBaseUrl = (state: AppState): string =>
  process.env.IN_CHROMECAST_MODE &&
  `https://${window.location.hostname}` !== state.config.session.dany_base_url
    ? '' // Cast DEV server
    : state.config.session.dany_base_url; // Dany Web, Cast Non-DEV

export const getVmAnalyticsUrl = (state: AppState): string =>
  deviceType === 'BROWSER'
    ? `${getBaseUrl(state)}/sling_analytics/da/vm/v2`
    : `http://analytics.sling.com/da/vm/v2`;

export const getBillingAnalyticsUrl = (state: AppState) =>
  deviceType === 'BROWSER'
    ? `${getBaseUrl(state)}/sling_analytics/analytics.gif`
    : `http://analytics.sling.com/analytics.gif`;

export const getLineupId = (receivers: Array<Receiver>, boxId: string): string => {
  let lineupId = '';
  if (receivers && receivers.length > 0) {
    const curReceiverInfo = receivers.find(item => item.receiver_id === boxId);
    lineupId = curReceiverInfo ? curReceiverInfo.lineup_hash : '';
  }
  return lineupId;
};

export const createJsonObj = (
  action: VmAction,
  uuid: string,
  boxId: string,
  boxLineupId: string = '',
) => {
  const obj: VmEvent = {
    ...(action.event || {}),
    uuid,
    boxId,
    boxLineupId: boxLineupId || ``,
  };
  return JSON.stringify(obj);
};

export const getAppInfoObj = (state: AppState, clientId: string = 'Web_Browser') => {
  const {
    app: { activeReceiver },
    config: { appName, releaseVersion },
    intl: { currentLocale },
    profiles: {
      currentProfile: { profile },
    },
    auth: { viewer },
    device: { deviceName, deviceOs, deviceId },
  } = state;

  const currentTime = moment().unix();

  return {
    unique_user: viewer?.euid,
    lang: currentLocale,
    profile_type: profile?.id,
    profile_id: viewer?.uuid,
    HHID: viewer?.uuid,
    uuid: viewer?.uuid,
    device_id: deviceId,
    os_name: deviceOs,
    os_ver: deviceOs,
    friendly_name: deviceName,
    partner: 'dish', // SGCommonConstants
    interval: '60', // always 60 in MD
    app_id: appName,
    client_version: releaseVersion,
    client_id: clientId,
    player_instance_id: deviceId,
    currentTime,
    client_timestamp: currentTime,
    finder_id: activeReceiver?.finderId,
    wan_lan: undefined, // WAN/LAN ?? Need clarification
  };
};

export const getAppInfoObjBillingAnaytics = (
  state: AppState,
  clientId: string = 'Web_Browser',
  tableName: string,
  uniqDeviceId: string,
) => {
  const {
    app: { activeReceiver },
    config: { appName, releaseVersion },
    auth: { viewer },
    device: { deviceName, deviceOs, deviceId },
  } = state;

  const currentTime = moment().unix();

  return {
    nagra_device_unique_id: viewer?.euid,
    uuid_hash: viewer?.uuid, // ? need to be checked and checnged to use hash instead of uuid
    uuid: viewer?.uuid,
    device_id: deviceId,
    os_name: deviceOs,
    os_ver: deviceOs,
    friendly_name: deviceName,
    partner: 'dish', // SGCommonConstants
    interval: '60', // always 60 in MD
    app_id: appName,
    client_version: releaseVersion,
    client_id: clientId,
    currentTime,
    client_timestamp: currentTime,
    wan_lan: undefined, // WAN/LAN ?? Need clarification
    client_primary_mac_adress: uniqDeviceId,
    receiver_id: activeReceiver?.receiverId,
    table_name: tableName,
  };
};

export const dispatchBillingAnalyticsEvent = (
  store: Store<AppState, BaseAction | ThunkAction, Dispatch>,
  data: any,
) => {
  const state = store.getState();
  const headers = {
    'Content-Type': 'application/x-www-form-urlencoded',
  };
  const url = getBillingAnalyticsUrl(state);
  const options = {
    url,
    state,
    data,
    headers,
  };

  get(options)
    .then(response => {
      if (response.ok) {
        console.log(`${url} sent successfuly`);
        if (deviceType !== 'BROWSER')
          store.dispatch(removeBillingEventFromStorage(JSON.stringify(data)));
      } else {
        throw Error(`Response status = ${response.status}`);
      }
    })
    .catch(error => {
      console.warn(`${url} sent error`, error);
      if (deviceType !== 'BROWSER') store.dispatch(addBillingEventToStorage(JSON.stringify(data)));
    });
};

export const dispatchVmAnalyticsEvent = (
  store: Store<AppState, BaseAction | ThunkAction, Dispatch>,
  data: any,
) => {
  const state = store.getState();
  const headers = {
    'Content-Type': 'application/x-www-form-urlencoded',
  };
  const url = getVmAnalyticsUrl(state);
  const options = {
    url,
    state,
    data,
    headers,
  };

  post(options)
    .then(() => {
      console.log(`${url} sent successfuly`);
      if (deviceType !== 'BROWSER') store.dispatch(removeVmEventFromStorage(JSON.stringify(data)));
    })
    .catch(error => {
      console.warn(`${url} sent error`, error);
      if (deviceType !== 'BROWSER') store.dispatch(addVmEventToStorage(JSON.stringify(data)));
    });
};

export const sendCachedVmAnalyticsEvents = (
  store: Store<AppState, BaseAction | ThunkAction, Dispatch>,
) => {
  const state = store.getState();
  const list = state.analytics.vmEvents;
  list.forEach(item => {
    dispatchVmAnalyticsEvent(store, JSON.parse(item));
  });
};

export const sendCachedBillingAnalyticsEvents = (
  store: Store<AppState, BaseAction | ThunkAction, Dispatch>,
) => {
  const state = store.getState();
  const list = state.analytics.billingEvents;
  list.forEach(item => {
    dispatchBillingAnalyticsEvent(store, JSON.parse(item));
  });
};

export const sendBillingAnalytics = (
  store: Store<AppState, BaseAction | ThunkAction, Dispatch>,
  tableName: string = '',
) => {
  const state = store.getState();
  const data = getAppInfoObjBillingAnaytics(state, getClientId(), tableName, getUniqueId());

  dispatchBillingAnalyticsEvent(store, data);
};

export const sendVmPlusAnalytics = (
  event: string,
  store: Store<AppState, BaseAction | ThunkAction, Dispatch>,
) => {
  const state = store.getState();
  const eventObj = typeof event === 'string' ? JSON.parse(event) : event;
  const appInfoObj = getAppInfoObj(state, getClientId());
  const data = { ...eventObj, ...appInfoObj };

  dispatchVmAnalyticsEvent(store, data);
};

export const syncAnalyticsEvents = (store: Store<AppState, BaseAction | ThunkAction, Dispatch>) => {
  if (deviceType === 'BROWSER') return;

  sendCachedVmAnalyticsEvents(store);
  sendCachedBillingAnalyticsEvents(store);
};
